import {
  DocumentEditorType,
  enableWidgetInternalStatusLog,
  logWidgetsIds,
  PtPermissions,
  WidgetConfiguration
} from '@pt/pt-models';
import { Observable, of } from 'rxjs';
import { Chapter } from 'it/primatoo/openapi/api/org/schemas/common/docs/_listtemplatechapterresponse/Chapter';
import { TreatmentWarning } from 'it/primatoo/openapi/api/org/schemas/treatment/TreatmentWarning';
import { ChapterStatus } from 'it/primatoo/openapi/api/org/schemas/common/docs/ChapterStatus';

export class WidgetInternalStatus {

  /**
   * se valorizzato visualizza i log solo dei widget con internalId inserito nella lista
   * @private
   */
  private readonly TEST_LOG_WIDGET_ID_FILTER = logWidgetsIds;
  private readonly enabledLog = enableWidgetInternalStatusLog;

  /**
   * questo valori arrivano dalla ricetta o dalla configurazione interna del widget e non sono modificabili
   * @private
   */
  private readonly _widgetId: number;
  private readonly _supportEditMode: boolean;
  private _isOptional: boolean;
  private readonly _enabledComment: boolean;
  private _actualEditModeWidgetId: number;
  private _activeChapter?: Chapter;
  private _configuration: WidgetConfiguration;
  private _warnings: TreatmentWarning[];
  private _isInitializedTemplate: boolean;
  private _hasValue: boolean;
  private _permissions: PtPermissions[];
  // TODO: gestire anche la validità
  private _validWidget: boolean;
  private _enabled: boolean;

  constructor(widgetId: number,
              supportEditMode: boolean,
              isOptional: boolean,
              isEnabled: boolean,
              enabledComment: boolean,
              widgetConfiguration: WidgetConfiguration) {
    const error = this.getErrorIfExist(widgetId, supportEditMode, isOptional, enabledComment, widgetConfiguration);
    this._validWidget = error === null;

    if (!this._validWidget) {
      this._errorMessage = of(error);
      this._viewMode = 'ERROR';
    }
    this._widgetId = widgetId;
    this._supportEditMode = supportEditMode;
    this._isOptional = isOptional;
    this._enabled = isEnabled;
    this._configuration = widgetConfiguration ? widgetConfiguration : {
      supportAutoSave: false,
      supportValidation: false
    };
    this._enabledComment = enabledComment;
    this._isAutoSave = false;
    this._isDraft = false;
    this._isLoading = false;
    this._isInitializedTemplate = false;
    this._isMyLocked = false;
    this._userRemoveOpt = false;

    this._showEditButton = false;
    this._showSaveButton = false;
    this._showReloadButton = false;
    this._showCommentButton = false;
    this._showCancelButton = false;
    this._showOptButton = false;
    this._disabledResetAllButton = false;

    this._disabledEditButton = true;
    this._disabledSaveButton = true;
    this._disabledReloadButton = true;
    this._disabledCommentButton = true;
    this._disabledCancelButton = true;
    this._disabledOptButton = true;
    this._isValid = false;
    this._warnings = [];
    this.checkButtonPermission();
  }

  private _viewMode: WidgetViewMode;

  get viewMode(): WidgetViewMode {
    return this._viewMode;
  }

  private _isValid: boolean;

  get activeChapter(): Chapter {
    return this._activeChapter;
  }

  get isValid(): boolean {
    return this._isValid;
  }

  private _showEditButton: boolean;

  get showEditButton(): boolean {
    return this._showEditButton;
  }

  private _showSaveButton: boolean;

  get showSaveButton(): boolean {
    return this._showSaveButton;
  }

  get warnings(): TreatmentWarning[] {
    return this._warnings;
  }

  setWarning(value: TreatmentWarning[]) {
    this._warnings = value;
  }

  private _showReloadButton: boolean;

  get showReloadButton(): boolean {
    return this._showReloadButton;
  }

  private _showCommentButton: boolean;

  get showCommentButton(): boolean {
    return this._showCommentButton;
  }

  private _showCancelButton: boolean;

  get showCancelButton(): boolean {
    return this._showCancelButton;
  }

  private _disabledResetAllButton: boolean;


  get disabledResetAllButton(): boolean {
    return this._disabledResetAllButton;
  }

  private _showOptButton: boolean;

  get showOptButton(): boolean {
    return this._showOptButton;
  }

  private _disabledEditButton: boolean;

  get disabledEditButton(): boolean {
    return this._disabledEditButton;
  }

  private _disabledSaveButton: boolean;

  get disabledSaveButton(): boolean {
    return this._disabledSaveButton;
  }

  private _disabledReloadButton: boolean;

  get disabledReloadButton(): boolean {
    return this._disabledReloadButton;
  }

  private _disabledCommentButton: boolean;

  get disabledCommentButton(): boolean {
    return this._disabledCommentButton;
  }

  /*
   * questi metodi sono le azioni che vanno a condizionare i valori dei predicati di questa classe
   */

  private _disabledCancelButton: boolean;

  get disabledCancelButton(): boolean {
    return this._disabledCancelButton;
  }

  private _disabledOptButton: boolean;

  get disabledOptButton(): boolean {
    return this._disabledOptButton;
  }

  private _docId: number;

  get docId(): number {
    return this._docId;
  }

  private _docType: DocumentEditorType;

  get docType(): DocumentEditorType {
    return this._docType;
  }

  private _isAutoSave: boolean;

  get isAutoSave(): boolean {
    return this._isAutoSave;
  }

  private _isDraft: boolean;

  get isDraft(): boolean {
    return this._isDraft;
  }

  get enabled(): boolean {
    return this._enabled;
  }

  private _isLoading: boolean;

  get isLoading(): boolean {
    return this._isLoading;
  }

  private _isDisabled: boolean;

  get isDisabled(): boolean {
    return this._isDisabled;
  }

  private _isMyLocked: boolean;

  get isMyLocked(): boolean {
    return this._isMyLocked;
  }

  private _userRemoveOpt: boolean;

  get userRemoveOpt(): boolean {
    return this._userRemoveOpt;
  }

  private _errorMessage: Observable<string>;

  get errorMessage(): Observable<string> {
    return this._errorMessage;
  }

  setStatus(isLoading: boolean,
            actualEditModeWidgetId: number,
            isInitializedTemplate: boolean,
            hasValue: boolean) {
    this._isLoading = isLoading ? isLoading : false;
    this._actualEditModeWidgetId = actualEditModeWidgetId;
    this._isInitializedTemplate = isInitializedTemplate ? isInitializedTemplate : false;
    this._hasValue = hasValue ? hasValue : false;

    this.checkButtonPermission();
  }

  setHasValue(_hasValue: boolean) {
    if (_hasValue !== this._hasValue) {
      this._hasValue = _hasValue ? _hasValue : false;
      this.checkButtonPermission();
    }
  }

  setUserRemoveOpt(userRemoveOpt: boolean) {
    if (userRemoveOpt !== this._userRemoveOpt) {
      this._userRemoveOpt = userRemoveOpt ? userRemoveOpt : false;
      this.checkButtonPermission();
    }
  }

  setIsLoading(isLoading: boolean) {
    if (isLoading !== this._isLoading) {
      this._isLoading = isLoading ? isLoading : false;
      this.checkButtonPermission();
    }
  }

  setIsAutoSave(isAutoSave: boolean) {
    if (isAutoSave !== this._isAutoSave) {
      this._isAutoSave = isAutoSave ? isAutoSave : false;
      this.checkButtonPermission();
    }
  }

  setIsValid(isValid: boolean) {
    if (isValid !== this._isValid) {
      this._isValid = isValid ? isValid : false;
      this.checkButtonPermission();
    }
  }

  setDocInfo(docId: number, docType: DocumentEditorType) {
    if (docId !== this._docId || docType !== this._docType) {
      this._docId = docId;
      this._docType = docType;
      this.checkButtonPermission();
    }
  }

  setIsDraft(isDraft: boolean) {
    if (isDraft !== this._isDraft) {
      this._isDraft = isDraft ? isDraft : false;
      this.checkButtonPermission();
    }
  }

  setChapter(chapter: Chapter) {
    switch (true) {
      case chapter?.id !== this._activeChapter?.id:
      case chapter?.currentStatus !== this._activeChapter?.currentStatus:
      case chapter?.completed !== this._activeChapter?.completed:
        this._activeChapter = chapter;
        break;
    }
    this.checkButtonPermission();
  }

  setOptional(opt: boolean) {
    if (opt !== this._isOptional) {
      this._isOptional = opt ? true : false;
      this.checkButtonPermission();
    }
  }

  setEnabled(enable: boolean) {
    if (enable !== this._enabled) {
      this._enabled = enable ? true : false;
      this.checkButtonPermission();
    }
  }

  setIsMyLocked(isMyLocked: boolean) {
    if (isMyLocked !== this._isMyLocked) {
      this._isMyLocked = isMyLocked ? isMyLocked : false;
      this.checkButtonPermission();
    }
  }


  setPermission(_permissions: PtPermissions[]) {
    if (JSON.stringify(_permissions) !== JSON.stringify(this._permissions)) {
      this._permissions = _permissions;
      this.checkButtonPermission();
    }
  }

  public checkButtonPermission() {
    if (this._validWidget) {
      this._showEditButton = this.checkShowEditButton();
      this._showSaveButton = this.checkShowSaveButton();
      this._showReloadButton = this.isDraft;
      this._showCommentButton = this.checkShowCommentButton();
      this._showCancelButton = this.checkShowCancelButton();
      this._showOptButton = this.checkShowOptButton();
      this._isDisabled = this.checkIsDisabled();

      this._disabledResetAllButton = !this.checkEnabledResetAllButton();
      this._disabledEditButton = this.checkDisabledEditButton() || this._isDisabled;
      this._disabledSaveButton = this.checkDisabledSaveButton() || this._isDisabled;
      this._disabledReloadButton = this.checkDisabledReloadButton() || this._isDisabled;
      this._disabledCommentButton = this.checkDisabledCommentButton() || this._isDisabled;
      this._disabledCancelButton = this.checkDisabledCancelButton() || this._isDisabled;
      this._disabledOptButton = this.checkDisabledOptButton() || this._isDisabled;
      this._viewMode = this.checkViewMode();
    }
  }

  private showLog(): boolean {
    return this.enabledLog && (this.TEST_LOG_WIDGET_ID_FILTER?.includes(this._widgetId));
  }

  private checkShowCommentButton(): boolean {
    return this._enabledComment;
  }

  private checkDisabledCommentButton(): boolean {
    return this._isLoading
      || !this._isInitializedTemplate
      || !this._enabledComment
      || !this._isMyLocked
      || (!this._isAutoSave && this._actualEditModeWidgetId && this._actualEditModeWidgetId !== this._widgetId)
      || !this.isValidChapterStatus();
  }

  private checkShowEditButton(): boolean {

    return !this._isAutoSave &&
      this._supportEditMode &&
      this._isDraft &&
      this.isValidChapterStatus() &&
      (this._actualEditModeWidgetId === null ||
        this._actualEditModeWidgetId === undefined ||
        this._actualEditModeWidgetId !== this._widgetId);
  }

  private checkDisabledEditButton(): boolean {
    return this._isLoading ||
      !this.isValidChapterStatus();
    !this._isInitializedTemplate ||
    !this.checkShowEditButton() ||
    !this._isMyLocked ||
    (
      this._actualEditModeWidgetId !== null &&
      this._actualEditModeWidgetId !== undefined
    );
  }

  private checkShowSaveButton(): boolean {
    return !this._isAutoSave &&
      this._supportEditMode &&
      this._isDraft &&
      this.isValidChapterStatus() &&
      // rispetto all'Edit Mode il save viene mostrato
      // se ho cliccato sull'EDIT e quindi ho il lock della risorsa
      this._isMyLocked &&
      (this._actualEditModeWidgetId === this._widgetId);
  }

  private checkEnabledResetAllButton(): boolean {

    let response = false;
    if (this._isDraft &&
      !this._isDisabled &&
      this._isInitializedTemplate &&
      !this._isLoading &&
      this._supportEditMode) {

      if (this._isAutoSave || this._isMyLocked) {
        response = true;
      }
    }
    return response;
  }

  private checkDisabledSaveButton(): boolean {
    return this._isLoading ||
      !this.isValidChapterStatus() ||
      !this._isInitializedTemplate ||
      !this.checkShowSaveButton() ||
      (this._configuration.supportValidation && !this._isValid);
  }

  private checkDisabledReloadButton(): boolean {
    return !this._isInitializedTemplate || !this.isDraft || this.checkShowSaveButton() || !this.isValidChapterStatus();
  }

  private checkShowCancelButton(): boolean {
    return this.checkShowSaveButton();
  }

  /**
   * ho lasciato la distinzione tra save e cancel perchè il save potrebbe in futuro
   * avere l'abilitazione se è validato il widget
   * @private
   */
  private checkDisabledCancelButton(): boolean {
    return this._isLoading || !this._isInitializedTemplate || !this.checkShowCancelButton() || !this.isValidChapterStatus();
  }

  private checkShowOptButton(): boolean {
    return this._isOptional &&
      this.isValidChapterStatus() &&
      this._supportEditMode &&
      this._isDraft &&
      !this._userRemoveOpt &&
      !this._hasValue;
  }

  private checkDisabledOptButton() {
    return !this._isMyLocked ||
      !this.isValidChapterStatus() || (
        !this.showOptButton &&
        !this._isDraft ||
        this._isLoading ||
        !this._isInitializedTemplate ||
        (
          this._actualEditModeWidgetId !== null &&
          this._actualEditModeWidgetId !== undefined
        )
      );
  }

  private checkViewMode(): WidgetViewMode {
    let response: WidgetViewMode = 'PREVIEW';
    // let actualCondition = "";
    switch (true) {
      case !this.isValidChapterStatus():
        response = 'PREVIEW';
        break;
      case this._isAutoSave && !this._configuration.supportAutoSave && this._configuration.supportValidation:
        this._errorMessage = of('Recipe is autosaved but widget supports validation!');
        response = 'ERROR';
        // actualCondition = "Autosave for widget that require validation";
        break;
      case this._isAutoSave && !this._configuration.supportAutoSave:
        this._errorMessage = of('Widget unsupport autosave!');
        response = 'ERROR';
        // actualCondition = "Widget not support autosave";
        break;
      case this._isAutoSave && (this._actualEditModeWidgetId !== null && this._actualEditModeWidgetId == undefined):
        this._errorMessage = of('Autosave active with unespected widget id ' + this._actualEditModeWidgetId + ' in edit mode!');
        response = 'ERROR';
        // actualCondition = "Autosave active with unespected widget id ";
        break;

      case this._actualEditModeWidgetId === this._widgetId && !this._supportEditMode:
        this._errorMessage = of('Widget not support edit mode!');
        response = 'ERROR';
        // actualCondition = "Not support Edit Mode";
        break;

      case this._actualEditModeWidgetId === this._widgetId && !this._isMyLocked:
        this._errorMessage = of('Widget in edit mode without lock!');
        response = 'ERROR';
        // actualCondition = "Edit Mode Without Lock";
        break;

      case this._isLoading:
        response = 'LOADING';
        // actualCondition = "Loading";
        break;

      case
      this._isDraft &&
      this._isMyLocked &&
      this._supportEditMode &&
      (
        (this._isAutoSave && this._configuration.supportAutoSave) ||
        this._actualEditModeWidgetId === this._widgetId
      ):
        // actualCondition = "Editor";
        response = 'EDIT';
        break;

      default:
        // actualCondition = 'Default case';
        response = 'PREVIEW';
    }
    return response;
  }

  private getErrorIfExist(widgetId: number, supportEditMode: boolean, isOptional: boolean, enabledComment: boolean, widgetConfiguration: WidgetConfiguration): string | null {

    switch (true) {

      case widgetId === null || widgetId === undefined:
        return 'Invalid widget id: ' + widgetId;

      case supportEditMode === null || supportEditMode === undefined:
        return 'Invalid supportEditMode: ' + supportEditMode;

      case isOptional === null || isOptional === undefined:
        return 'Invalid isOptional: ' + isOptional;

      case enabledComment === null || enabledComment === undefined:
        return 'Invalid enabledComment: ' + supportEditMode;

      case widgetConfiguration === null || widgetConfiguration === undefined || widgetConfiguration.supportAutoSave === null || widgetConfiguration.supportAutoSave === undefined:
        return 'Invalid widgetConfiguration: ' + JSON.stringify(widgetConfiguration);
    }

    return null;
  }

  private checkIsDisabled() {
    switch (true) {

      case !this.isValidChapterStatus():
      case !this._isInitializedTemplate:
      case this._isLoading:
      case !this._isMyLocked:
      case this._actualEditModeWidgetId !== undefined && this._actualEditModeWidgetId !== null && this._actualEditModeWidgetId !== this._widgetId:
        return true;

      case this._isAutoSave && this._configuration.supportAutoSave && this._isMyLocked:
        return false;

      default:
        return false;
    }
  }

  private isValidChapterStatus() {
    if (this._activeChapter && this._activeChapter.currentStatus) {
      return this._activeChapter.currentStatus === ChapterStatus.WORKING;
    }
    return true;
  }
}

export type WidgetViewMode = 'LOADING' | 'ERROR' | 'PREVIEW' | 'EDIT';
